import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag, Dot } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { Chips, DataTable, DeskHead, PhoneBar, SearchBox, Segmented, type Col } from './kit/Kit';
import { useUrlState } from './kit/hooks';
import { useAccountsAndRoles } from './usersData';
import { effectivePerms, scopeBrief } from './permissions';
import { Headline } from './RoleDetail';
import { RoleCreateSheet } from './RoleCreateSheet';
import { AccountCreateSheet } from './AccountCreateSheet';
import { AccountSettingsSheet } from './AccountSettingsSheet';
import { RoleDeleteFlow } from './RoleDeleteFlow';
import { activeState, roleKind } from './status';
import type { Role, UserRow } from './types';
import s from './UsersPage.module.css';

type Tab = 'roles' | 'accounts';

function RoleCard({ role, to, selected }: { role: Role; to: string; selected?: boolean }) {
  const kind = roleKind(role.isPreset);
  const sc = scopeBrief(role, effectivePerms(role));
  return (
    <Link to={to} className={`${s.card} ${selected ? s.sel : ''}`}>
      <div className={s.ch}>
        <b>{role.name}</b>
        <small className="tnum">{role.code}</small>
        <Tag tone={kind.tone} sm>{kind.label}</Tag>
      </div>
      <p className={s.sum}><Headline role={role} /></p>
      <div className={s.cf}>
        {role.isActive ? <Dot tone={sc.tone}>{sc.label}</Dot> : <Dot tone="grey">已停用</Dot>}
        <span className={s.muted}>账号 <b className="tnum">{role.userCount}</b> 个</span>
      </div>
    </Link>
  );
}

function AccountCard({ u, role }: { u: UserRow; role?: Role }) {
  const st = activeState(u.isActive);
  const sc = role ? scopeBrief(role, effectivePerms(role)) : null;
  return (
    <Link to={`/users/accounts/${u.id}`} className={s.card}>
      <div className={s.ch}>
        <b>{u.name}</b>
        <Tag tone={st.tone} sm>{st.label}</Tag>
      </div>
      <div className={s.accountMeta}>
        <span><i>账号</i><b className="tnum">{u.username}</b></span>
        <span className={s.accountLastLogin}><i>最近登录</i><b className="tnum em">—</b></span>
        <span><i>角色</i><b>{role?.name || u.roleDefinition?.name || u.roleCode || u.role}</b></span>
        {sc && <Dot tone={sc.tone}>{sc.label}</Dot>}
      </div>
    </Link>
  );
}

/**
 * 账号与角色（permissions-*.html A1 / A7）。
 * - 角色：手机卡片（人话摘要在卡面上），iPad / 桌面表格；
 * - 账号：手机卡片，iPad / 桌面表格；点开是账号详情。
 * 「能干什么」全部由当前生效权限（授予 − 限制项）自动生成。
 */
export function UsersPage() {
  const ff = useFormFactor();
  const nav = useNavigate();
  const can = useSession((st) => st.can);
  const [sp, set] = useUrlState();
  const [createOpen, setCreateOpen] = useState(false);
  const [phoneSearchOpen, setPhoneSearchOpen] = useState(false);
  const [phoneAccountSearchOpen, setPhoneAccountSearchOpen] = useState(false);
  const [phoneRolePickerOpen, setPhoneRolePickerOpen] = useState(false);
  const [accountCreateOpen, setAccountCreateOpen] = useState(false);
  const [settingsAccount, setSettingsAccount] = useState<UserRow | null>(null);
  const [deleteRole, setDeleteRole] = useState<Role | null>(null);
  const data = useAccountsAndRoles();
  const tabs = [
    data.canRoles && { key: 'roles', label: '角色', count: data.rolesState.data ? data.roles.length : null },
    data.canUsers && { key: 'accounts', label: '账号', count: data.usersState.data ? data.users.length : null },
  ].filter(Boolean) as { key: Tab; label: string; count: number | null }[];
  const tab: Tab = (tabs.find((t) => t.key === sp.get('tab'))?.key || tabs[0]?.key || 'roles') as Tab;
  const status = sp.get('status') || 'all';
  const roleFilter = sp.get('role') || 'all';
  const q = sp.get('q') || '';

  if (!tabs.length) {
    return <Empty icon="lock" title="没有查看账号或角色的权限">需要「查看账号列表」或「查看角色列表」权限，请联系管理员。</Empty>;
  }

  const seg = tabs.length > 1 && <Segmented items={tabs} value={tab} onChange={(k) => set({ tab: k === tabs[0].key ? undefined : k, id: undefined, status: undefined, role: undefined, q: undefined, page: undefined })} />;

  /* ------------------------------ 角色 ------------------------------ */
  const roles = data.roles;
  const presetN = roles.filter((r) => r.isPreset).length;
  const accountsN = roles.reduce((a, r) => a + r.userCount, 0);
  const rolesMeta = `${presetN} 个预置 · ${roles.length - presetN} 个自定义 · 共 ${accountsN} 个账号`;
  const roleRows = roles.filter((role) => !q || `${role.name} ${role.code}`.toLowerCase().includes(q.toLowerCase()));
  const compareLink = tab === 'roles' && data.canRoles && roles.length > 1 && <Link className={s.compareLink} to="/users/compare"><span>对比角色</span></Link>;
  const historyLink = can('audit.read') && data.canRoles && <Link className={s.compareLink} to="/users/history">变更记录</Link>;
  const createRoleButton = tab === 'roles' && can('roles.create') && <button type="button" className={s.createRoleButton} onClick={() => setCreateOpen(true)}>＋ 新建角色</button>;
  const closeCreate = () => { setCreateOpen(false); if (sp.has('createRoleFrom')) set({ createRoleFrom: undefined }); };
  const roleCreateSheet = (createOpen || sp.has('createRoleFrom')) && <RoleCreateSheet roles={roles} initialTemplateId={sp.get('createRoleFrom') || undefined} canCreate={can('roles.create')} onClose={closeCreate} onCreated={(role) => { setCreateOpen(false); nav(`/users/roles/${role.id}`); }} />;
  const createAccountButton = tab === 'accounts' && can('users.create') && <button type="button" className={s.createRoleButton} onClick={() => setAccountCreateOpen(true)}>＋ 新建账号</button>;
  const phoneTabs = [
    data.canRoles && { key: 'roles', label: '角色', count: data.rolesState.data ? roles.length : null, to: '/users?tab=roles', active: tab === 'roles' },
    data.canUsers && { key: 'accounts', label: '账号', count: data.usersState.data ? data.users.length : null, to: '/users?tab=accounts', active: tab === 'accounts' },
    can('audit.read') && data.canRoles && { key: 'history', label: '变更记录', count: null, to: '/users/history', active: false },
    data.canRoles && { key: 'migration', label: '迁移对照', count: data.rolesState.data ? roles.length : null, to: '/users/migration-preview', active: false },
  ].filter(Boolean) as { key: string; label: string; count: number | null; to: string; active: boolean }[];
  const phoneNav = <nav className={s.phoneTabs} aria-label="账号权限">
    {phoneTabs.map((item) => <Link key={item.key} to={item.to} aria-current={item.active ? 'page' : undefined} className={item.active ? s.phoneTabOn : ''}>
      <span>{item.label}</span>{item.count != null && <i className="tnum">{item.count}</i>}
    </Link>)}
  </nav>;
  const accountCreateSheet = accountCreateOpen && <AccountCreateSheet roles={roles} canCreate={can('users.create')} onClose={() => setAccountCreateOpen(false)} onCreated={(account) => { setAccountCreateOpen(false); nav(`/users/accounts/${account.id}`); }} />;
  const rolesBody = (() => {
    const st = data.rolesState.error ? data.rolesState : null;
    if (st?.error) return <ErrorState title="角色加载失败" message={st.error.message} detail="GET /api/roles" onRetry={st.reload} />;
    if ((data.rolesState.loading || data.usersState.loading) && !roles.length) return <div className={s.pad}><Skeleton /><Skeleton /><Skeleton lines={3} /></div>;
    if (!roles.length) return <Empty icon="shield" title="还没有角色">新建角色请在老版操作。</Empty>;
    return null;
  })();

  const roleCols: Col<Role>[] = [
    { key: 'name', title: '角色', w: 170, fix: true, render: (r) => <div className={s.cell2}><b>{r.name}</b><small className="tnum">{r.code}</small></div> },
    { key: 'kind', title: '类型', w: 84, render: (r) => { const k = roleKind(r.isPreset); return <Tag tone={k.tone} sm>{k.label}</Tag>; } },
    { key: 'n', title: '账号数', w: 76, align: 'r', render: (r) => r.userCount },
    { key: 'sum', title: '能干什么（自动生成）', w: 460, render: (r) => <Headline role={r} clamp /> },
    { key: 'scope', title: '数据范围', w: 130, render: (r) => { if (!r.isActive) return <Dot tone="grey">已停用</Dot>; const b = scopeBrief(r, effectivePerms(r)); return <Dot tone={b.tone}>{b.label}</Dot>; } },
    { key: 'actions', title: '操作', w: 130, align: 'c', fixRight: true, render: (r) => (
      <div className={s.rowActions}>
        <button type="button" aria-label={`查看角色${r.name}`} onClick={(event) => { event.stopPropagation(); nav(`/users/roles/${r.id}`); }}><Icon name="eye" size={16} /></button>
        {!r.isPreset && can('roles.delete') && <button type="button" className={s.rowDelete} aria-label={`删除角色${r.name}`} onClick={(event) => { event.stopPropagation(); setDeleteRole(r); }}><Icon name="trash" size={16} /></button>}
      </div>
    ) },
  ];
  const roleToolbar = <div className={s.roleToolbar}>
    <SearchBox value={q} onSearch={(value) => set({ q: value || undefined })} placeholder="角色名称" className={s.search} />
    <span className={s.toolbarSpacer} />
    <button type="button" className={s.exportRoles} onClick={() => {
      const quote = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`;
      const rows = [['角色', '编码', '类型', '账号数', '有效权限', '限制项', '仓库范围'], ...roles.map((role) => [role.name, role.code, role.isPreset ? '预置' : '自定义', role.userCount, [...effectivePerms(role)].join('、'), role.restrictions.join('、'), role.warehouseIds?.join('、') || '全部仓库'])];
      const csv = `\uFEFF${rows.map((row) => row.map(quote).join(',')).join('\r\n')}`;
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = '角色权限清单.csv'; anchor.click();
      URL.revokeObjectURL(url);
    }}>导出权限清单</button>
  </div>;

  /* ------------------------------ 账号 ------------------------------ */
  const kw = q.toLowerCase();
  const users = data.users.filter(
    (u) =>
      (status === 'all' || (status === 'on' ? u.isActive : !u.isActive)) &&
      (roleFilter === 'all' || (u.roleCode || u.role) === roleFilter) &&
      (!kw || u.name.toLowerCase().includes(kw) || u.username.toLowerCase().includes(kw)),
  );
  const accountPageSize = 6;
  const accountPageCount = Math.max(1, Math.ceil(users.length / accountPageSize));
  const accountPage = Math.min(Math.max(Number(sp.get('page')) || 1, 1), accountPageCount);
  const pageUsers = users.slice((accountPage - 1) * accountPageSize, accountPage * accountPageSize);
  const onN = data.users.filter((u) => u.isActive).length;
  const statusChips = (
    <Chips value={status} onChange={(k) => set({ status: k === 'all' ? undefined : k, page: undefined })}
      items={[{ key: 'all', label: '全部', count: data.users.length }, { key: 'on', label: '启用', count: onN }, { key: 'off', label: '停用', count: data.users.length - onN }]} />
  );
  const roleCodes = [...new Set(data.users.map((u) => u.roleCode || u.role))];
  const roleFilterControl = <label className={s.roleFilter}><select aria-label="按角色筛选账号" value={roleFilter} onChange={(event) => set({ role: event.target.value === 'all' ? undefined : event.target.value, page: undefined })}><option value="all">角色：全部</option>{roleCodes.map((code) => <option key={code} value={code}>{data.roles.find((role) => role.code === code)?.name || code}</option>)}</select></label>;
  const phoneRoleFilter = <div className={s.phoneRoleFilterWrap}>
    <button type="button" className={s.phoneRoleFilter} aria-expanded={phoneRolePickerOpen} onClick={() => setPhoneRolePickerOpen((open) => !open)}>
      <span>{roleFilter === 'all' ? '角色' : data.roles.find((role) => role.code === roleFilter)?.name || roleFilter}</span><Icon name="chev" size={14} className={s.downChevron} />
    </button>
    {phoneRolePickerOpen && <div className={s.phoneRolePopover} role="group" aria-label="按角色筛选账号">
      {[{ code: 'all', name: '全部角色', count: data.users.length }, ...roleCodes.map((code) => ({ code, name: data.roles.find((role) => role.code === code)?.name || code, count: data.users.filter((user) => (user.roleCode || user.role) === code).length }))].map((item) => <button key={item.code} type="button" aria-pressed={roleFilter === item.code} onClick={() => { set({ role: item.code === 'all' ? undefined : item.code, page: undefined }); setPhoneRolePickerOpen(false); }}><span>{item.name}</span><i className="tnum">{item.count}</i></button>)}
    </div>}
  </div>;
  const accountsBody = (() => {
    if (data.usersState.error) return <ErrorState title="账号加载失败" message={data.usersState.error.message} detail="GET /api/users" onRetry={data.usersState.reload} />;
    if (data.usersState.loading && !data.users.length) return <div className={s.pad}><Skeleton /><Skeleton /><Skeleton lines={3} /></div>;
    if (!users.length) return <Empty icon="user" title="没有符合条件的账号">换个关键词，或者清空筛选再看看。</Empty>;
    return null;
  })();
  const accountCols: Col<UserRow>[] = [
    { key: 'username', title: '账号', w: 150, fix: true, render: (u) => <span className="tnum">{u.username}</span> },
    { key: 'name', title: '姓名', w: 130, render: (u) => u.name },
    { key: 'role', title: '角色', w: 170, render: (u) => { const role = data.roleOf(u); const code = u.roleCode || u.role; const tone = code === 'ADMIN' ? 'blue' : code === 'FOLLOWER' ? 'violet' : code === 'WAREHOUSE' ? 'orange' : code === 'SALES' ? 'green' : code === 'VIEWER' ? 'none' : 'amber'; return <Tag tone={tone}>{role?.name || u.roleDefinition?.name || code}</Tag>; } },
    { key: 'scope', title: '数据范围', w: 190, render: (u) => { const r = data.roleOf(u); if (!r) return <span className="em">—</span>; const b = scopeBrief(r, effectivePerms(r)); return <Tag tone={b.tone === 'grey' ? 'none' : b.tone} sm>{b.label}</Tag>; } },
    { key: 'st', title: '状态', w: 110, render: (u) => { const a = activeState(u.isActive); return <Tag tone={a.tone} sm>{a.label}</Tag>; } },
    { key: 'lastLogin', title: '最近登录', w: 170, render: () => <span className="em">—</span> },
    { key: 'actions', title: '操作', w: 130, align: 'c', fixRight: true, render: (u) => <div className={s.rowActions}><button type="button" aria-label={`查看账号${u.name}`} onClick={(event) => { event.stopPropagation(); nav(`/users/accounts/${u.id}`); }}><Icon name="eye" size={16} /></button>{can('users.edit', 'users.status.write', 'users.password.reset') && <button type="button" aria-label={`账号设置${u.name}`} onClick={(event) => { event.stopPropagation(); setSettingsAccount(u); }}><Icon name="set" size={16} /></button>}</div> },
  ];
  const search = <SearchBox value={q} onSearch={(v) => set({ q: v || undefined, page: undefined })} placeholder="账号 / 姓名" className={s.search} />;
  const roleSearch = <SearchBox value={q} onSearch={(v) => set({ q: v || undefined, page: undefined })} placeholder="角色名称" className={s.search} />;
  const exportAccountsButton = tab === 'accounts' && <button type="button" className={s.exportRoles} onClick={() => {
    const quote = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`;
    const rows = [['账号', '姓名', '角色', '数据范围', '状态'], ...users.map((user) => { const role = data.roleOf(user); return [user.username, user.name, role?.name || user.roleDefinition?.name || user.roleCode || user.role, role ? scopeBrief(role, effectivePerms(role)).label : '—', user.isActive ? '启用' : '停用']; })];
    const csv = `\uFEFF${rows.map((row) => row.map(quote).join(',')).join('\r\n')}`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a'); anchor.href = url; anchor.download = '账号清单.csv'; anchor.click(); URL.revokeObjectURL(url);
  }}>导出</button>;

  /* ------------------------------ 手机 ------------------------------ */
  if (ff === 'phone') {
    const phoneActions = <div className={s.phoneActions}>
      {tab === 'roles' && <button type="button" aria-label={phoneSearchOpen ? '关闭搜索' : '搜索角色'} onClick={() => setPhoneSearchOpen((open) => !open)}><Icon name={phoneSearchOpen ? 'x' : 'srch'} size={20} /></button>}
      {tab === 'accounts' && <button type="button" aria-label={phoneAccountSearchOpen ? '关闭搜索' : '搜索账号'} onClick={() => setPhoneAccountSearchOpen((open) => !open)}><Icon name={phoneAccountSearchOpen ? 'x' : 'srch'} size={20} /></button>}
      {tab === 'roles' && can('roles.create') && <button type="button" aria-label="新建角色" onClick={() => setCreateOpen(true)}>＋</button>}
      {tab === 'accounts' && can('users.create') && <button type="button" aria-label="新建账号" onClick={() => setAccountCreateOpen(true)}>＋</button>}
    </div>;
    return (
      <div className={s.phone}>
        <PhoneBar back="/apps" backLabel="应用" title="账号权限" right={phoneActions} />
        {phoneNav}
        {tab === 'accounts' && <div className={s.accountPhoneBar}>{statusChips}{phoneRoleFilter}</div>}
        <div className={s.pbody}>
          {tab === 'roles' ? (
            <>
              {phoneSearchOpen && <div className={s.psearch}>{roleSearch}</div>}
              <div className={s.listbar}><span className={s.rolesMeta}>{rolesMeta}</span><span className={s.listActions}>{compareLink}</span></div>
              <p className={s.roleHint}>角色是「一键填一组权限」的模板，填完即与模板脱钩 —— 改模板不影响已建角色。</p>
              {rolesBody || (roleRows.length ? roleRows.map((r) => <RoleCard key={r.id} role={r} to={`/users/roles/${r.id}`} />) : <Empty icon="shield" title="没有符合条件的角色">换个角色名称，或者清空搜索再看看。</Empty>)}
              <p className={s.hint}>「能干什么」由当前权限自动生成，不是手写的；红字是看不到的内容。</p>
            </>
          ) : (
            <>
              {phoneAccountSearchOpen && <div className={s.psearch}>{search}</div>}
              <div className={`${s.listbar} ${s.accountListbar}`}><span>{users.length} 个账号 · 启用 {users.filter((user) => user.isActive).length} · 停用 {users.length - users.filter((user) => user.isActive).length}</span><button type="button" className={s.accountSort} disabled title="V1 共用账号数据未记录最近登录时间">最近登录 <Icon name="chev" size={14} className={s.downChevron} /></button></div>
              {accountsBody || users.map((u) => <AccountCard key={u.id} u={u} role={data.roleOf(u)} />)}
            </>
          )}
        </div>
        {roleCreateSheet}
        {accountCreateSheet}
      </div>
    );
  }

  /* ------------------------------ iPad：角色表格 ------------------------------ */
  if (ff === 'tablet' && tab === 'roles') {
    return (
      <div className={s.desk}>
        <DeskHead title="角色" sub={rolesMeta} className={s.roleHeaderWide}>{seg}{compareLink}{historyLink}{createRoleButton}</DeskHead>
        <div className={s.roleToolbar}>
          {roleSearch}
          <span className={s.toolbarSpacer} />
        </div>
        {rolesBody ? <div className={s.dbody}>{rolesBody}</div> : roles.length ? <DataTable cols={roleCols} rows={roles} rowKey={(r) => r.id} onRow={(r) => nav(`/users/roles/${r.id}`)} minWidth={1000} /> : <div className={s.dbody}><Empty icon="shield" title="没有符合条件的角色">换个角色名称，或者清空搜索再看看。</Empty></div>}
        <p className={s.tfoot}>共 {roles.length} 个角色 · 「能干什么」一列由当前权限（授予 − 限制项）自动生成，红字是看不到的内容</p>
        {roleCreateSheet}
        {accountCreateSheet}
      </div>
    );
  }

  /* ------------------------------ 桌面 / iPad 账号 ------------------------------ */
  return (
    <div className={s.desk}>
      <DeskHead title={tab === 'roles' ? '角色' : '账号'} sub={tab === 'roles' ? rolesMeta : <><span>每个账号绑定一个角色。改角色权限会立即影响绑定它的所有账号</span><span className={s.accountCount}>{data.users.length} 个 · 启用 {onN} · 停用 {data.users.length - onN}</span></>} className={s.roleHeaderWide}>
        {tab === 'roles' ? <>{compareLink}{createRoleButton}</> : <>{exportAccountsButton}{createAccountButton}</>}
      </DeskHead>
      {tab === 'roles' ? (
        <>
          {roleToolbar}
          {rolesBody ? <div className={s.dbody}>{rolesBody}</div> : roleRows.length ? <DataTable cols={roleCols} rows={roleRows} rowKey={(r) => r.id} onRow={(r) => nav(`/users/roles/${r.id}`)} minWidth={1140} /> : <div className={s.dbody}><Empty icon="shield" title="没有符合条件的角色">换个角色名称，或者清空搜索再看看。</Empty></div>}
          <p className={s.tfoot}>共 {roles.length} 个角色 · 「能干什么」一列由当前生效权限（授予 − 限制项）自动生成，红字是看不到的内容</p>
        </>
      ) : (
        <>
          <div className={s.accountPanel}>
            <div className={s.accountToolbar}>{search}{roleFilterControl}{statusChips}</div>
            <div className={s.accountRows}>{accountsBody || <DataTable cols={accountCols} rows={pageUsers} rowKey={(u) => u.id} onRow={(u) => nav(`/users/accounts/${u.id}`)} minWidth={1080} />}</div>
            <footer className={s.accountPager}>
              <span>第 {users.length ? (accountPage - 1) * accountPageSize + 1 : 0}–{Math.min(accountPage * accountPageSize, users.length)} 条 / 共 {users.length} 个账号</span>
              <div className={s.pageButtons}>
                <button type="button" aria-label="上一页" disabled={accountPage <= 1} onClick={() => set({ page: String(accountPage - 1) })}>‹</button>
                {Array.from({ length: accountPageCount }, (_, index) => index + 1).map((page) => <button key={page} type="button" aria-current={page === accountPage ? 'page' : undefined} className={page === accountPage ? s.pageCurrent : undefined} onClick={() => set({ page: String(page) })}>{page}</button>)}
                <button type="button" aria-label="下一页" disabled={accountPage >= accountPageCount} onClick={() => set({ page: String(accountPage + 1) })}>›</button>
              </div>
            </footer>
          </div>
        </>
      )}
      {roleCreateSheet}
      {accountCreateSheet}
      {settingsAccount && <AccountSettingsSheet account={settingsAccount} roles={roles} canEdit={can('users.edit')} canStatus={can('users.status.write')} canReset={can('users.password.reset')} onClose={() => setSettingsAccount(null)} onSaved={data.usersState.reload} />}
      {tab === 'roles' && deleteRole && <RoleDeleteFlow role={deleteRole} roles={roles} members={data.membersOf(deleteRole)} canMigrate={can('users.edit')} onClose={() => setDeleteRole(null)} onAccountsChanged={() => { data.usersState.reload(); data.rolesState.reload(); }} onDeleted={() => { setDeleteRole(null); data.rolesState.reload(); data.usersState.reload(); }} />}
    </div>
  );
}
